// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The cross-reference chain — where every earlier version of a PDF is
 * recorded, and the only honest way to find them.
 *
 * WHY NOT JUST SEARCH FOR `%%EOF`. Because it is wrong most of the time. Six
 * official PDFs published by the IRS, HUD and the US Government Publishing
 * Office were measured while this tool was designed: five of the six contain
 * more than one `%%EOF`, and in every one of those five at least one of them is
 * NOT an earlier version of the document. Three causes account for nearly all
 * of it:
 *
 *   - Linearisation. A web-optimised file carries a first-page cross-reference
 *     table near byte 0 with its own `%%EOF`. Truncating there yields a
 *     ~500-byte file with zero pages that still contains /Root — so the obvious
 *     "does this look like a PDF?" check does not catch it.
 *   - Attachments. A PDF that merely holds another PDF (an email-to-PDF
 *     gateway, a portfolio, a combined bundle) contains the inner file's
 *     `%%EOF` markers, its `startxref`, its /Linearized dictionary and its
 *     /ByteRange. All of them read as the outer document's if you scan bytes.
 *   - Ordinary content. `%%EOF` can appear inside any stream.
 *
 * So this file never scans. It starts at the last `startxref`, parses the
 * section it names, and follows /Prev backwards. A revision boundary is a
 * section reachable on that chain and nothing else. An attachment's internals
 * are unreachable by construction, which is a stronger guarantee than masking
 * byte ranges could give.
 *
 * ISO 32000-1:2008 §7.5.4 (cross-reference table), §7.5.8 (cross-reference
 * streams), §7.5.6 (incremental updates), Annex F (linearisation).
 */

import { flateDecode } from './inflate.ts';
import { applyPredictor, DEFAULT_PREDICTOR, type PredictorParams } from './predictor.ts';
import {
  asciiBytes,
  indexOfBytes,
  isDict,
  isStream,
  lastIndexOfBytes,
  parseIndirectObjectAt,
  parseObject,
  PdfName,
  PdfRef,
  PdfSyntaxError,
  readToken,
  skipWhite,
  type PdfDict,
  type PdfStream,
  type PdfValue,
} from './lexer.ts';

/** Type 1: at a byte offset. Type 2: inside an object stream. Type 0: free. */
export interface XrefEntry {
  type: 0 | 1 | 2;
  /** Byte offset (type 1) or the containing object stream's number (type 2). */
  offset: number;
  /** Generation (type 1) or index within the object stream (type 2). */
  gen: number;
}

export interface XrefSection {
  /** Byte offset of the `xref` keyword or of the cross-reference stream object. */
  offset: number;
  kind: 'table' | 'stream';
  entries: Map<number, XrefEntry>;
  trailer: PdfDict;
  /** /Prev — the offset of the previous section, or null at the chain's root. */
  prev: number | null;
  /** /XRefStm — a hybrid-reference file's parallel cross-reference stream. */
  xrefStm: number | null;
  /** Byte offset of the `%%EOF` that terminates this section's revision. */
  eofOffset: number;
  /** One past the last byte of that `%%EOF` and its trailing end-of-line. */
  revisionEnd: number;
}

export const EOF_MARKER = asciiBytes('%%EOF');
const STARTXREF = asciiBytes('startxref');

export class XrefError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'XrefError';
  }
}

/** Byte offset named by the file's last `startxref`. */
export function findLastStartXref(buf: Uint8Array): number {
  // §7.5.5 requires the trailer to be within the last 1024 bytes, but files in
  // the wild carry junk after %%EOF, so search the whole tail generously
  // before giving up.
  const at = lastIndexOfBytes(buf, STARTXREF);
  if (at === -1) throw new XrefError('this file has no startxref, so it has no cross-reference table');
  const tok = readToken(buf, at + STARTXREF.length);
  if (!/^\d+$/.test(tok.token)) {
    throw new XrefError('the last startxref is not followed by a byte offset');
  }
  return Number.parseInt(tok.token, 10);
}

/** Offset of the first `%%EOF` at or after `from`, and one past its line end. */
function eofAfter(buf: Uint8Array, from: number): { eofOffset: number; revisionEnd: number } {
  const at = indexOfBytes(buf, EOF_MARKER, from);
  if (at === -1) {
    // A file whose final section has no %%EOF is malformed but readable; treat
    // the end of the file as the boundary rather than refusing everything.
    return { eofOffset: buf.length, revisionEnd: buf.length };
  }
  let end = at + EOF_MARKER.length;
  if (buf[end] === 0x0d) end += 1;
  if (buf[end] === 0x0a) end += 1;
  return { eofOffset: at, revisionEnd: end };
}

function num(dict: PdfDict, key: string): number | null {
  const v = dict.get(key);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** ISO 32000-1 §7.5.4 — the classic `xref` table. */
function parseXrefTable(buf: Uint8Array, offset: number): XrefSection {
  const kw = readToken(buf, offset);
  if (kw.token !== 'xref') {
    throw new XrefError(`byte ${offset} should begin a cross-reference table but reads "${kw.token}"`);
  }

  const entries = new Map<number, XrefEntry>();
  let pos = kw.end;

  for (;;) {
    const peek = readToken(buf, pos);
    if (peek.token === 'trailer') {
      pos = peek.end;
      break;
    }
    if (!/^\d+$/.test(peek.token)) {
      throw new XrefError(
        `the cross-reference table at byte ${offset} has "${peek.token}" where a subsection header should be`,
      );
    }
    const start = Number.parseInt(peek.token, 10);
    const countTok = readToken(buf, peek.end);
    if (!/^\d+$/.test(countTok.token)) {
      throw new XrefError(`the cross-reference subsection at ${start} has no entry count`);
    }
    const count = Number.parseInt(countTok.token, 10);
    if (count < 0 || count > 50_000_000) {
      throw new XrefError(`the cross-reference subsection at ${start} claims ${count} entries`);
    }

    pos = countTok.end;
    for (let k = 0; k < count; k += 1) {
      // §7.5.4 fixes entries at 20 bytes, but writers emit 19 (a bare LF) and
      // pad inconsistently, so the fields are read as tokens instead.
      const a = readToken(buf, pos);
      const b = readToken(buf, a.end);
      const c = readToken(buf, b.end);
      if (!/^\d+$/.test(a.token) || !/^\d+$/.test(b.token) || (c.token !== 'n' && c.token !== 'f')) {
        throw new XrefError(
          `entry ${k} of the cross-reference subsection at ${start} is malformed ("${a.token} ${b.token} ${c.token}")`,
        );
      }
      const objNum = start + k;
      // First writer wins: a section's own entries take precedence, and an
      // earlier /Prev section must never overwrite them.
      if (!entries.has(objNum)) {
        entries.set(objNum, {
          type: c.token === 'n' ? 1 : 0,
          offset: Number.parseInt(a.token, 10),
          gen: Number.parseInt(b.token, 10),
        });
      }
      pos = c.end;
    }
  }

  const trailerVal = parseObject(buf, pos).value;
  if (!isDict(trailerVal)) throw new XrefError(`the trailer at byte ${pos} is not a dictionary`);

  const { eofOffset, revisionEnd } = eofAfter(buf, pos);
  return {
    offset,
    kind: 'table',
    entries,
    trailer: trailerVal,
    prev: num(trailerVal, 'Prev'),
    xrefStm: num(trailerVal, 'XRefStm'),
    eofOffset,
    revisionEnd,
  };
}

/** Reads the /DecodeParms that apply to a FlateDecode cross-reference stream. */
function predictorParams(dict: PdfDict): PredictorParams {
  let parms = dict.get('DecodeParms') ?? dict.get('DP');
  if (Array.isArray(parms)) parms = parms.find((p) => isDict(p)) ?? null;
  if (!isDict(parms)) return DEFAULT_PREDICTOR;
  return {
    predictor: num(parms, 'Predictor') ?? 1,
    colors: num(parms, 'Colors') ?? 1,
    bitsPerComponent: num(parms, 'BitsPerComponent') ?? 8,
    columns: num(parms, 'Columns') ?? 1,
  };
}

function filterNames(dict: PdfDict): string[] {
  const f = dict.get('Filter') ?? dict.get('F');
  if (f instanceof PdfName) return [f.name];
  if (Array.isArray(f)) return f.filter((x): x is PdfName => x instanceof PdfName).map((x) => x.name);
  return [];
}

/** Decodes a stream's raw bytes. Only the filters a skeleton can contain. */
export function decodeStream(stream: PdfStream): Uint8Array {
  const filters = filterNames(stream.dict);
  if (filters.length === 0) return stream.raw;
  if (filters.length > 1) {
    throw new XrefError(`a chained filter (${filters.join(' + ')}) is not supported here`);
  }
  const [filter] = filters;
  if (filter === 'FlateDecode' || filter === 'Fl') {
    return applyPredictor(flateDecode(stream.raw), predictorParams(stream.dict));
  }
  throw new XrefError(`the ${filter} filter is not supported here`);
}

/** ISO 32000-1 §7.5.8 — the PDF 1.5+ cross-reference stream. */
function parseXrefStream(buf: Uint8Array, offset: number): XrefSection {
  const numTok = readToken(buf, offset);
  if (!/^\d+$/.test(numTok.token)) {
    throw new XrefError(
      `byte ${offset} is neither a cross-reference table nor a cross-reference stream object`,
    );
  }
  const objNum = Number.parseInt(numTok.token, 10);
  const value = parseIndirectObjectAt(buf, offset, objNum);
  if (!isStream(value)) {
    throw new XrefError(`object ${objNum} at byte ${offset} is not a stream`);
  }

  const dict = value.dict;
  const type = dict.get('Type');
  if (!(type instanceof PdfName) || type.name !== 'XRef') {
    throw new XrefError(`object ${objNum} at byte ${offset} is not a /Type /XRef stream`);
  }

  const w = dict.get('W');
  if (!Array.isArray(w) || w.length < 3 || !w.every((x) => typeof x === 'number')) {
    throw new XrefError(`the cross-reference stream at byte ${offset} has no usable /W array`);
  }
  const widths = (w as number[]).map((x) => Math.trunc(x));
  const rowLength = widths.reduce((a, b) => a + b, 0);
  if (rowLength <= 0) throw new XrefError(`the cross-reference stream at byte ${offset} has a zero-width /W`);

  const size = num(dict, 'Size');
  let index: number[];
  const rawIndex = dict.get('Index');
  if (Array.isArray(rawIndex) && rawIndex.length >= 2 && rawIndex.every((x) => typeof x === 'number')) {
    index = (rawIndex as number[]).map((x) => Math.trunc(x));
  } else {
    if (size === null) throw new XrefError(`the cross-reference stream at byte ${offset} has no /Size`);
    index = [0, size];
  }

  const data = decodeStream(value);
  const rows = Math.floor(data.length / rowLength);

  const entries = new Map<number, XrefEntry>();
  let row = 0;
  for (let s = 0; s + 1 < index.length; s += 2) {
    const first = index[s];
    const count = index[s + 1];
    for (let k = 0; k < count; k += 1, row += 1) {
      if (row >= rows) {
        throw new XrefError(
          `the cross-reference stream at byte ${offset} declares ${
            index.filter((_, i) => i % 2 === 1).reduce((a, b) => a + b, 0)
          } entries but decodes to only ${rows}`,
        );
      }
      const base = row * rowLength;
      let f = 0;
      const fields: number[] = [];
      for (const width of widths) {
        let v = 0;
        for (let bi = 0; bi < width; bi += 1) v = v * 256 + data[base + f + bi];
        fields.push(v);
        f += width;
      }
      // §7.5.8.2: "if the first element is zero, the type field shall not be
      // present, and shall default to type 1".
      const entryType = widths[0] === 0 ? 1 : fields[0];
      const objectNumber = first + k;
      if (!entries.has(objectNumber) && (entryType === 0 || entryType === 1 || entryType === 2)) {
        entries.set(objectNumber, {
          type: entryType as 0 | 1 | 2,
          offset: fields[1],
          gen: fields[2],
        });
      }
    }
  }

  const { eofOffset, revisionEnd } = eofAfter(buf, value.start + value.raw.length);
  return {
    offset,
    kind: 'stream',
    entries,
    trailer: dict,
    prev: num(dict, 'Prev'),
    xrefStm: null,
    eofOffset,
    revisionEnd,
  };
}

export function parseXrefSection(buf: Uint8Array, offset: number): XrefSection {
  if (offset < 0 || offset >= buf.length) {
    throw new XrefError(`a cross-reference offset points to byte ${offset}, outside a ${buf.length}-byte file`);
  }
  const first = skipWhite(buf, offset);
  if (buf[first] === 0x78 /* x */) return parseXrefTable(buf, offset);
  return parseXrefStream(buf, offset);
}

export interface XrefChain {
  /** Newest section first; the last entry is the original save. */
  sections: XrefSection[];
  /**
   * A section whose /Prev points FORWARD rather than backward. That is the
   * signature of a linearised file's first-page cross-reference table
   * (Annex F): it sits near byte 0 and its /Prev names the main table at the
   * end. Both belong to one save, so the pair must never be counted as two.
   */
  linearFirstPageOffsets: Set<number>;
  /** Non-fatal problems worth telling the reader about. */
  warnings: string[];
}

const MAX_SECTIONS = 512;

export function parseXrefChain(buf: Uint8Array): XrefChain {
  const start = findLastStartXref(buf);
  const sections: XrefSection[] = [];
  const seen = new Set<number>();
  const linearFirstPageOffsets = new Set<number>();
  const warnings: string[] = [];

  let offset: number | null = start;
  while (offset !== null) {
    if (seen.has(offset)) {
      warnings.push(
        `The cross-reference chain loops back to byte ${offset}. Reading stopped there rather than going round forever.`,
      );
      break;
    }
    if (sections.length >= MAX_SECTIONS) {
      warnings.push(
        `This file has more than ${MAX_SECTIONS} cross-reference sections. Only the most recent ${MAX_SECTIONS} were read.`,
      );
      break;
    }
    seen.add(offset);

    let section: XrefSection;
    try {
      section = parseXrefSection(buf, offset);
    } catch (err) {
      if (sections.length === 0) throw err;
      warnings.push(
        `The chain of earlier versions stops at byte ${offset}: ${(err as Error).message}. ` +
          'Anything before that point cannot be read.',
      );
      break;
    }

    if (section.prev !== null && section.prev > section.offset) {
      linearFirstPageOffsets.add(section.offset);
    }

    sections.push(section);

    // A hybrid-reference file (§7.5.8.4) carries a parallel cross-reference
    // stream for the same save. Merge its entries in — it is not a revision.
    if (section.xrefStm !== null && !seen.has(section.xrefStm)) {
      seen.add(section.xrefStm);
      try {
        const hybrid = parseXrefSection(buf, section.xrefStm);
        for (const [k, v] of hybrid.entries) if (!section.entries.has(k)) section.entries.set(k, v);
      } catch (err) {
        warnings.push(`This file's parallel cross-reference stream could not be read: ${(err as Error).message}`);
      }
    }

    offset = section.prev;
  }

  return { sections, linearFirstPageOffsets, warnings };
}

/**
 * Merges the chain into one lookup, newest wins. This is the table the
 * document as it stands now is read through.
 */
export function mergeEntries(sections: XrefSection[]): Map<number, XrefEntry> {
  const merged = new Map<number, XrefEntry>();
  for (const section of sections) {
    for (const [k, v] of section.entries) if (!merged.has(k)) merged.set(k, v);
  }
  return merged;
}

/** The /Root reference, taken from the newest section that names one. */
export function findRoot(sections: XrefSection[]): PdfRef | null {
  for (const section of sections) {
    const root = section.trailer.get('Root');
    if (root instanceof PdfRef) return root;
  }
  return null;
}

export function findTrailerRef(sections: XrefSection[], key: string): PdfRef | null {
  for (const section of sections) {
    const v = section.trailer.get(key);
    if (v instanceof PdfRef) return v;
  }
  return null;
}

export function findTrailerValue(sections: XrefSection[], key: string): PdfValue | undefined {
  for (const section of sections) {
    const v = section.trailer.get(key);
    if (v !== undefined) return v;
  }
  return undefined;
}

export { PdfSyntaxError };
